import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiQuery } from '@/hooks/use-api-query';
import { qk } from '@/lib/query/keys';

export interface MembersResponse {
  members: unknown[];
  invites: unknown[];
}

export function useMembers(wsId: string | undefined, sessionScoped: boolean) {
  return useApiQuery<MembersResponse>(
    qk.workspace(wsId ?? '', 'members'),
    `/workspaces/${wsId}/members`,
    { enabled: !!wsId && sessionScoped },
  );
}

function useMembersInvalidate(wsId: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: qk.workspace(wsId, 'members') });
}

// Membership changes (assign / remove / role) also reshape the org Team roster
// (TeamMemberRow.workspaces), so invalidate that cache too when we know the org.
function useMembersAndTeamInvalidate(wsId: string, orgId?: string) {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: qk.workspace(wsId, 'members') }),
      ...(orgId ? [qc.invalidateQueries({ queryKey: qk.org(orgId, 'team') })] : []),
    ]);
}

export function useInviteMember(wsId: string) {
  const invalidate = useMembersInvalidate(wsId);
  return useApiMutation<void, { email: string; role: 'member' | 'admin' }>(
    (vars) => ({
      path: `/workspaces/${wsId}/members`,
      init: {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(vars),
      },
      fallback: 'Failed to invite member',
    }),
    { onSuccess: invalidate },
  );
}

export function useAssignMember(wsId: string, orgId?: string) {
  const invalidate = useMembersAndTeamInvalidate(wsId, orgId);
  return useApiMutation<
    void,
    { organizationMemberPublicId: string; role: 'member' | 'admin' }
  >(
    (vars) => ({
      path: `/workspaces/${wsId}/members/assign`,
      init: {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(vars),
      },
      fallback: 'Failed to assign member',
    }),
    { onSuccess: invalidate },
  );
}

export function useRemoveMember(wsId: string, orgId?: string) {
  const invalidate = useMembersAndTeamInvalidate(wsId, orgId);
  return useApiMutation<void, { memberId: string }>(
    ({ memberId }) => ({
      path: `/workspaces/${wsId}/members/${memberId}`,
      init: { method: 'DELETE' },
      fallback: 'Failed to remove member',
    }),
    { onSuccess: invalidate },
  );
}

export function useChangeMemberRole(wsId: string, orgId?: string) {
  const invalidate = useMembersAndTeamInvalidate(wsId, orgId);
  return useApiMutation<void, { memberId: string; role: string }>(
    ({ memberId, role }) => ({
      path: `/workspaces/${wsId}/members/${memberId}`,
      init: {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ role }),
      },
      fallback: 'Failed to update role',
    }),
    { onSuccess: invalidate },
  );
}

export function useCancelInvite(wsId: string) {
  const invalidate = useMembersInvalidate(wsId);
  return useApiMutation<void, { inviteId: string }>(
    ({ inviteId }) => ({
      path: `/workspaces/${wsId}/invites/${inviteId}`,
      init: { method: 'DELETE' },
      fallback: 'Failed to cancel invite',
    }),
    { onSuccess: invalidate },
  );
}
